import {
  Checkbox,
  Grid,
  Group,
  MultiSelect,
  NumberInput,
  Radio,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { DEFAULT_DISCOUNT_VALUES, discountIssue } from '../../helpers/discountConfig';
import useFetch from '../../helpers/useFetch';
import CountrySelect from '../CountrySelect';
import DatePickerControl from '../DatePicker';
import ErrorMessage from '../ErrorMessage';
import CouponFormSection from './CouponFormSection';
import PartnerAuthorizationAlert from './PartnerAuthorizationAlert';

export const TARGET_SEARCH_DEBOUNCE_MS = 300;

const DISCOUNT_TYPES = [
  { value: 'fixedAmount', label: 'Fixed amount' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'freeShipping', label: 'Free shipping' },
];

const VALUE_LABELS = {
  fixedAmount: 'Value off',
  percentage: 'Percent off',
  freeShipping: 'Maximum shipping rate',
};

const PREFIX_HELP =
  "Coupon prefixes can contain letters, numbers and hyphens. Coupon codes will be generated by adding 8 random letters and numbers to the end of your prefix. If you don't specify a prefix, the coupon code will be 8 random letters and numbers. We don't include I, O, 0 and 1 so codes are easier to use.";

const toNumberOrNull = (inputValue) => {
  if (typeof inputValue === 'number') {
    return Number.isFinite(inputValue) ? inputValue : null;
  }
  if (typeof inputValue !== 'string' || inputValue.trim() === '') {
    return null;
  }
  const parsedNumber = Number(inputValue);
  return Number.isFinite(parsedNumber) ? parsedNumber : null;
};

const ConfigNumberInput = ({ value, onChange, ...rest }) => {
  const [rawValue, setRawValue] = useState(value ?? '');
  const committedValue = value ?? null;
  const displayedValue = toNumberOrNull(rawValue) === committedValue ? rawValue : (value ?? '');
  return (
    <NumberInput
      {...rest}
      value={displayedValue}
      onChange={(inputValue) => {
        setRawValue(inputValue);
        onChange(toNumberOrNull(inputValue));
      }}
    />
  );
};

const fallbackTargetLabel = (kind, id) => {
  const lastIdSegment = String(id).split('/').pop();
  return `${kind === 'collections' ? 'Collection' : 'Product'} ${lastIdSegment}`;
};

const SettingsRow = ({ label, children }) => (
  <Grid align="flex-start">
    <Grid.Col span={{ base: 12, sm: 2 }}>
      <Text fw={500}>{label}</Text>
    </Grid.Col>
    <Grid.Col span={{ base: 12, sm: 10 }}>{children}</Grid.Col>
  </Grid>
);

const FieldHint = ({ message, edited }) => (
  <Text size="xs" mt={4} c={edited ? 'red' : 'dimmed'}>
    {message}
  </Text>
);

const GeneratedCouponSettings = ({ value: config, onChange, nameField }) => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();
  const currency = useSelector((state) => state.domain.domain?.currency);

  const [targetSearch, setTargetSearch] = useState('');
  const [debouncedTargetSearch] = useDebouncedValue(targetSearch, TARGET_SEARCH_DEBOUNCE_MS);
  const [targetOptions, setTargetOptions] = useState([]);
  const [targetLabels, setTargetLabels] = useState({});
  const [targetLoadError, setTargetLoadError] = useState();
  const [editedFields, setEditedFields] = useState(() => new Set());

  const markEdited = (field) =>
    setEditedFields((previousFields) =>
      previousFields.has(field) ? previousFields : new Set([...previousFields, field])
    );

  const isFreeShipping = config.type === 'freeShipping';
  const appliesToKind = isFreeShipping ? 'order' : config.appliesTo.kind;

  useEffect(() => {
    if (appliesToKind === 'order') {
      return undefined;
    }
    let cancelled = false;
    const query = queryString.stringify({ type: appliesToKind, search: debouncedTargetSearch });
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons/targets?${query}`)
      .then(({ targets }) => {
        if (cancelled) {
          return;
        }
        const fetchedTargets = targets || [];
        setTargetOptions(fetchedTargets);
        setTargetLoadError(undefined);
        setTargetLabels((previousLabels) => {
          const mergedLabels = { ...previousLabels };
          for (const target of fetchedTargets) {
            mergedLabels[target.id] = target.title;
          }
          return mergedLabels;
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setTargetLoadError(error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId, appliesToKind, debouncedTargetSearch]);

  const update = (changes) => onChange({ ...config, ...changes });

  const changeType = (type) => {
    const nextConfig = { ...config, type, value: DEFAULT_DISCOUNT_VALUES[type] };
    if (type === 'freeShipping') {
      nextConfig.appliesTo = { kind: 'order', ids: [] };
      nextConfig.combinesWith = { ...config.combinesWith, shippingDiscounts: false };
    } else {
      nextConfig.countries = { all: true };
    }
    onChange(nextConfig);
  };

  const changeAppliesToKind = (kind) => {
    setTargetSearch('');
    setTargetOptions([]);
    update({ appliesTo: { kind, ids: [] } });
  };

  const changeTargets = (ids) => {
    markEdited('targets');
    const titles = {};
    for (const selectedId of ids) {
      const title = targetLabels[selectedId] || config.appliesTo.titles?.[selectedId];
      if (title) {
        titles[selectedId] = title;
      }
    }
    update({ appliesTo: { kind: appliesToKind, ids, titles } });
  };

  const changeExpirationKind = (kind) => {
    if (kind === 'afterDuration') {
      update({ expiration: { kind, days: 30, hours: 0, date: null } });
      return;
    }
    update({ expiration: { kind, days: null, hours: null, date: null } });
  };

  const targetData = () => {
    const optionsById = new Map();
    for (const target of targetOptions) {
      optionsById.set(target.id, { value: target.id, label: target.title });
    }
    for (const selectedId of config.appliesTo.ids || []) {
      if (!optionsById.has(selectedId)) {
        optionsById.set(selectedId, {
          value: selectedId,
          label:
            targetLabels[selectedId] ||
            config.appliesTo.titles?.[selectedId] ||
            fallbackTargetLabel(appliesToKind, selectedId),
        });
      }
    }
    return [...optionsById.values()];
  };

  const issue = discountIssue(config);
  const hintFor = (field) =>
    issue?.field === field ? <FieldHint message={issue.message} edited={editedFields.has(field)} /> : null;
  const minimumPurchase = config.minimumPurchase;
  const requiresMinimum = minimumPurchase.kind !== 'none';

  return (
    <Stack gap={0}>
      <CouponFormSection title="Coupon details">
        {nameField && <SettingsRow label="Name">{nameField}</SettingsRow>}
        <SettingsRow label="Prefix">
          <TextInput
            aria-label="Prefix"
            value={config.prefix || ''}
            placeholder="e.g. WELCOME-"
            description={PREFIX_HELP}
            maxLength={20}
            onChange={(event) => {
              markEdited('prefix');
              update({ prefix: event.target.value });
            }}
          />
          {hintFor('prefix')}
        </SettingsRow>
      </CouponFormSection>
      <CouponFormSection title="Discount">
        <SettingsRow label="Discount type">
          <SegmentedControl data={DISCOUNT_TYPES} value={config.type} onChange={changeType} />
        </SettingsRow>
        <SettingsRow label={VALUE_LABELS[config.type]}>
          <ConfigNumberInput
            aria-label={VALUE_LABELS[config.type]}
            value={config.value}
            min={0}
            max={config.type === 'percentage' ? 100 : undefined}
            decimalScale={2}
            description={isFreeShipping ? 'Leave empty to cover any shipping rate.' : undefined}
            rightSection={
              <Text size="sm" c="dimmed">
                {config.type === 'percentage' ? '%' : currency || ''}
              </Text>
            }
            onChange={(numberValue) => {
              markEdited('value');
              update({ value: numberValue });
            }}
          />
          {hintFor('value')}
        </SettingsRow>

        {isFreeShipping ? (
          <SettingsRow label="Country eligibility">
            <Radio.Group
              value={config.countries?.all === false ? 'selected' : 'all'}
              onChange={(choice) => update({ countries: choice === 'all' ? { all: true } : { all: false, codes: [] } })}
            >
              <Stack gap="xs">
                <Radio value="all" label="All countries" />
                <Radio value="selected" label="Selected countries" />
              </Stack>
            </Radio.Group>
            {config.countries?.all === false && (
              <Stack mt="xs">
                <CountrySelect
                  selected={config.countries.codes || []}
                  onChange={(codes) => {
                    markEdited('targets');
                    update({ countries: { all: false, codes: codes.map((code) => code.toUpperCase()) } });
                  }}
                />
              </Stack>
            )}
            {hintFor('targets')}
          </SettingsRow>
        ) : (
          <SettingsRow label="Applies to">
            <Radio.Group value={appliesToKind} onChange={changeAppliesToKind}>
              <Stack gap="xs">
                <Radio value="order" label="Entire order" />
                <Radio value="products" label="Specific products" />
                <Radio value="collections" label="Specific collections" />
              </Stack>
            </Radio.Group>
            {appliesToKind !== 'order' && (
              <Stack mt="xs">
                <MultiSelect
                  aria-label={appliesToKind === 'products' ? 'Products' : 'Collections'}
                  placeholder={appliesToKind === 'products' ? 'Search products...' : 'Search collections...'}
                  searchable
                  searchValue={targetSearch}
                  onSearchChange={setTargetSearch}
                  filter={({ options }) => options}
                  data={targetData()}
                  value={config.appliesTo.ids || []}
                  nothingFoundMessage="Nothing found"
                  onChange={changeTargets}
                />
                {targetLoadError &&
                  (targetLoadError.code === 'PARTNER_AUTHORIZATION_REQUIRED' ? (
                    <PartnerAuthorizationAlert
                      title="Releva needs permission to read your catalog"
                      message={targetLoadError.message}
                      authorizationUrl={targetLoadError.authorizationUrl}
                    />
                  ) : (
                    <ErrorMessage message={targetLoadError.message} />
                  ))}
              </Stack>
            )}
            {hintFor('targets')}
          </SettingsRow>
        )}

        <SettingsRow label="Minimum purchase">
          <Checkbox
            label="Require minimum purchase"
            checked={requiresMinimum}
            onChange={(event) =>
              update({
                minimumPurchase: event.currentTarget.checked
                  ? { kind: 'subtotal', value: null }
                  : { kind: 'none', value: null },
              })
            }
          />
          {requiresMinimum && (
            <Stack mt="xs" gap="xs">
              <Radio.Group
                value={minimumPurchase.kind}
                onChange={(kind) => update({ minimumPurchase: { kind, value: null } })}
              >
                <Group>
                  <Radio value="subtotal" label="Minimum purchase amount" />
                  <Radio value="quantity" label="Minimum quantity of items" />
                </Group>
              </Radio.Group>
              <ConfigNumberInput
                aria-label={minimumPurchase.kind === 'quantity' ? 'Minimum quantity' : 'Minimum amount'}
                value={minimumPurchase.value}
                min={minimumPurchase.kind === 'quantity' ? 1 : 0}
                allowDecimal={minimumPurchase.kind !== 'quantity'}
                decimalScale={2}
                rightSection={
                  <Text size="sm" c="dimmed">
                    {minimumPurchase.kind === 'quantity' ? '' : currency || ''}
                  </Text>
                }
                onChange={(numberValue) => {
                  markEdited('minimumPurchase');
                  update({ minimumPurchase: { kind: minimumPurchase.kind, value: numberValue } });
                }}
              />
            </Stack>
          )}
          {hintFor('minimumPurchase')}
        </SettingsRow>
      </CouponFormSection>
      <CouponFormSection title="Combinations">
        <SettingsRow label="Can combine with">
          <Stack gap="xs">
            <Checkbox
              label="Product and collection coupons"
              checked={config.combinesWith.productDiscounts}
              onChange={(event) =>
                update({ combinesWith: { ...config.combinesWith, productDiscounts: event.currentTarget.checked } })
              }
            />
            <Checkbox
              label="Entire order coupons"
              checked={config.combinesWith.orderDiscounts}
              onChange={(event) =>
                update({ combinesWith: { ...config.combinesWith, orderDiscounts: event.currentTarget.checked } })
              }
            />
            {!isFreeShipping && (
              <Checkbox
                label="Free shipping coupons"
                checked={config.combinesWith.shippingDiscounts}
                onChange={(event) =>
                  update({ combinesWith: { ...config.combinesWith, shippingDiscounts: event.currentTarget.checked } })
                }
              />
            )}
          </Stack>
        </SettingsRow>
      </CouponFormSection>
      <CouponFormSection title="Active dates">
        <SettingsRow label="Activation">
          <Radio.Group value={config.activation.kind} onChange={(kind) => update({ activation: { kind, date: null } })}>
            <Stack gap="xs">
              <Radio value="atSend" label="At send time" />
              <Radio value="date" label="On a specific date" />
            </Stack>
          </Radio.Group>
          {config.activation.kind === 'date' && (
            <DatePickerControl
              mt="xs"
              showTimeSelect
              aria-label="Activation date"
              placeholder="Pick a date and time"
              value={config.activation.date}
              onChange={(date) => {
                markEdited('activation');
                update({ activation: { kind: 'date', date } });
              }}
            />
          )}
          {hintFor('activation')}
        </SettingsRow>
        <SettingsRow label="Expiration">
          <Radio.Group value={config.expiration.kind} onChange={changeExpirationKind}>
            <Stack gap="xs">
              <Radio value="afterOneYear" label="After 1 year" />
              <Radio value="afterDuration" label="After a certain number of days/hours" />
              <Radio value="date" label="On a specific date" />
            </Stack>
          </Radio.Group>
          {config.expiration.kind === 'afterDuration' && (
            <Group mt="xs" grow>
              <ConfigNumberInput
                label="Days"
                value={config.expiration.days}
                min={0}
                allowDecimal={false}
                onChange={(numberValue) => {
                  markEdited('expiration');
                  update({ expiration: { ...config.expiration, days: numberValue } });
                }}
              />
              <ConfigNumberInput
                label="Hours"
                value={config.expiration.hours}
                min={0}
                allowDecimal={false}
                onChange={(numberValue) => {
                  markEdited('expiration');
                  update({ expiration: { ...config.expiration, hours: numberValue } });
                }}
              />
            </Group>
          )}
          {config.expiration.kind === 'date' && (
            <DatePickerControl
              mt="xs"
              showTimeSelect
              aria-label="Expiration date"
              placeholder="Pick a date and time"
              value={config.expiration.date}
              onChange={(date) => {
                markEdited('expiration');
                update({ expiration: { ...config.expiration, date } });
              }}
            />
          )}
          {hintFor('expiration')}
        </SettingsRow>
      </CouponFormSection>
    </Stack>
  );
};

export default GeneratedCouponSettings;
